// The module's semantic colour ramp. ONE definition, imported everywhere.
//
// WHY THIS FILE EXISTS. The same four meanings -- good, waiting, wrong, new --
// were coloured in three separate places: the asset state badge in
// assetTableHelpers, the work order status chip and the per-serial verdict in
// workOrderHelpers, plus loose literals in MovementBuilder, SerialReportTable
// and WorkOrderDetailView. The copies had drifted: THREE greens for one
// meaning (#1F7A4D, #1B7F3B, #1B7F4B), FOUR ambers (#8A6100, #8A5B00,
// #8A5A06, #6B4A00) and TWO error backgrounds (#FDECEA, #FBEDEB). Nobody chose
// those differences -- they are transcription drift, and they are the kind a
// reviewer cannot catch by eye because each one looks right on its own.
//
// WHY IT IS NOT IN ThemeProperties. That file carries the product's brand and
// greys and has no semantic ramp; CustomTag, the shared tag component, takes
// its colours as PROPS, so every caller in the app supplies its own. Claiming
// a product-wide token set here would mean auditing every other module's
// chips, which is a different job. This is the Asset Management module's ramp,
// in the module, and any other module is welcome to it.
//
// WHY THE HEXES ARE THE ONES THEY ARE. Where copies disagreed, the value kept
// is the one that already appears MOST WIDELY -- in the module for the
// semantic pairs, and across the whole product for the neutral greys, which is
// why neutral is #6B7280 on #F3F4F6 (used in three or more files elsewhere)
// rather than the badge table's own #5B5B6B on #F4F4F7 (used in none).
//
// A BORDER IS NOT DEFINED FOR EVERY ROLE. Only the work order status chip
// draws one, and only for the four roles below that have it. Inventing a blue
// or grey border purely for symmetry would add the thirty-ninth colour to a
// file whose whole point is removing colours.

export const STATUS_PALETTE = {
    // Approved, available, eligible to move.
    success: { fg: "#1B7F3B", bg: "#E9F7EE", border: "#B7E3C6" },

    // Waiting on someone, or repeated. The stronger of the two ambers.
    warning: { fg: "#8A5B00", bg: "#FFF4DA", border: "#F0D9A0" },

    // AUTO-APPROVED, and a deliberate second amber rather than drift.
    //
    // A type with requiresApproval = 0 is written straight through and nobody
    // checked it. That is not a failure, so it is not red; it is not a
    // signed-off approval either, so it must not look identical to one in a
    // list. It sits beside genuinely pending rows, which are also amber, so it
    // is a SOFTER amber -- the same hue, one step back, which reads as "less
    // urgent than the row above" rather than as a different state.
    warningSoft: { fg: "#8A5B00", bg: "#FFF8E7", border: "#EBD5A8" },

    // Rejected, ineligible, refused. #930500 is already the module's danger
    // ink and appears across the product.
    error: { fg: "#930500", bg: "#FDECEA", border: "#F3C2BD" },

    // An EXPECTED outcome that is not a success: a serial that will be
    // registered by this work order. Blue, not amber -- there is nothing to
    // look at twice.
    // The border is here for the same reason every other ramp entry has
    // one: a consumer that needs an outline must not invent its own, which is
    // how three ramps grew in the first place.
    info: { fg: "#1F4E8C", bg: "#EAF2FD", border: "#BBD4F5" },

    // No state recorded, or a state this build does not know. Never "no
    // styling": an unstyled chip reads as a rendering fault.
    neutral: { fg: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB" },
};

// The softer surfaces these ramps sit on when a whole PANEL, rather than a
// chip, carries the meaning -- the failed-serial report's card, the
// unreadable-rows list. Kept here so a panel and the chips inside it cannot
// drift apart.
export const STATUS_SURFACE = {
    error: "#FFF9F8",
    warning: "#FFF8E7",
};

// Body text inside one of those panels: the same hue as the ramp's ink, a
// step lighter, because a paragraph set in chip ink is too heavy to read.
export const STATUS_BODY_INK = {
    error: "#8A3A36",
    warning: "#6B4A00",
};

// assetState.badgeColorTheme is a free VARCHAR(32) seeded per org (see the
// STEP 2 block in app/database/asset/migration.sql), so the seed's own names
// and the obvious synonyms both resolve. Anything unrecognised falls back to
// neutral rather than to no styling at all.
const BADGE_THEME_ALIASES = {
    green: "success",
    ok: "success",
    orange: "warning",
    yellow: "warning",
    red: "error",
    danger: "error",
    critical: "error",
    blue: "info",
    primary: "info",
    grey: "neutral",
    gray: "neutral",
    default: "neutral",
};

/** One role name (or an org's synonym for it) -> {fg, bg, border?}. */
// OWN PROPERTIES ONLY. `role` is an org's free-text assetState.badgeColorTheme,
// so "constructor", "toString" and "__proto__" are values a user can type --
// and a bare lookup returns a function for each of them, which renders as an
// unstyled chip. That is the precise failure the neutral fallback exists to
// prevent, reached through the one door it did not cover.
const own = (table, key) =>
    Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

export const statusTheme = (role) => {
    // THE EXACT NAME FIRST, before any normalising. The lower-casing below is
    // for values an ORG typed into assetState.badgeColorTheme, but the roles
    // this module passes are camelCase -- and "warningSoft" lower-cased is
    // "warningsoft", which matches nothing and silently returned the neutral
    // grey. That turned every auto-approved work order into an unstyled chip.
    const exact = own(STATUS_PALETTE, role);
    if (exact) return exact;

    const key = String(role || "").trim().toLowerCase();
    return (
        own(STATUS_PALETTE, key)
        || own(STATUS_PALETTE, own(BADGE_THEME_ALIASES, key))
        || STATUS_PALETTE.neutral
    );
};
